import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasHeadingLevelGap, headings, readDocument } from "@/office/word/document";
import { picturesWithoutAltText, readPictures } from "@/office/word/media";
import { hasHeaderRow, readTables } from "@/office/word/tables";

export const lektionBarrierefreiheitspruefung: Lesson = {
  id: "pruefung",
  title: "Die Barrierefreiheitsprüfung",
  summary: "Word findet die meisten Hürden selbst – man muss sie nur laufen lassen.",
  durationMinutes: 9,
  learningGoals: [
    "Die Barrierefreiheitsprüfung ausführen und lesen",
    "Die häufigsten Befunde einordnen",
    "Ein Dokument ohne Hürden abliefern",
  ],
  steps: [
    {
      id: "pruefung-starten",
      title: "Die Prüfung starten",
      explanation: [
        "Word bringt eine eigene Prüfung mit: „Überprüfen“ → „Barrierefreiheit prüfen“. " +
          "Sie durchsucht das Dokument nach Hürden und listet sie am rechten Rand auf, " +
          "getrennt nach „Fehler“, „Warnungen“ und „Tipps“.",
        "Zu jedem Befund erklärt Word, warum es ein Problem ist und wie es sich beheben " +
          "lässt – ein Klick auf den Eintrag springt zur betroffenen Stelle.",
        "Praktisch ist die Einstellung „Barrierefreiheit während der Arbeit prüfen“ " +
          "unten im Bereich. Dann meldet Word Probleme laufend in der Statusleiste, " +
          "statt sie erst am Ende auf einen Schlag zu präsentieren.",
        "Die drei häufigsten Befunde kennst du bereits aus diesem Kurs: fehlende " +
          "Alternativtexte bei Bildern, Tabellen ohne Überschriftenzeile und Text, der " +
          "wie eine Überschrift aussieht, aber keine ist.",
      ],
      keyPoints: [
        "Überprüfen → Barrierefreiheit prüfen.",
        "Fehler sind echte Hürden, Warnungen und Tipps sind Verbesserungen.",
        "„Während der Arbeit prüfen“ meldet Probleme laufend.",
      ],
      task: {
        instruction:
          "Bereite ein Dokument vor, das die drei häufigsten Hürden vermeidet: " +
          "Überschriften über Formatvorlagen ohne Ebenensprünge, Bilder mit " +
          "Alternativtext und Tabellen mit Überschriftenzeile.",
        hint: "Lass zusätzlich Words eigene Prüfung laufen – sie findet Dinge, die dieses Add-in nicht sehen kann.",
        check: async () => {
          const snapshot = await readDocument();
          const bilder = await readPictures();
          const tabellen = await readTables();
          const ueberschriften = headings(snapshot);

          if (snapshot.filledParagraphs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Baue zuerst ein kleines Dokument auf.");
          }

          const ohneAltText = picturesWithoutAltText(bilder);
          const ohneKopfzeile = tabellen.filter((tabelle) => !hasHeaderRow(tabelle));

          return fromCriteria(
            [
              criterion(
                `Es gibt echte Überschriften (gefunden: ${ueberschriften.length})`,
                ueberschriften.length >= 2,
              ),
              criterion("Keine übersprungene Überschriftenebene", !hasHeadingLevelGap(snapshot)),
              criterion(
                `Kein Bild ohne Alternativtext (${bilder.length} Bilder, davon ohne: ${ohneAltText.length})`,
                ohneAltText.length === 0,
              ),
              criterion(
                `Keine Tabelle ohne Überschriftenzeile (${tabellen.length} Tabellen, davon ohne: ${ohneKopfzeile.length})`,
                ohneKopfzeile.length === 0,
              ),
            ],
            {
              success: "Die drei häufigsten Hürden sind ausgeräumt.",
              failure: "Es sind noch Hürden im Dokument:",
              hint: "Bilder und Tabellen sind nur dann ein Kriterium, wenn welche vorhanden sind – ein Dokument ohne beides erfüllt diese Punkte automatisch.",
            },
          );
        },
      },
    },
    {
      id: "was-die-pruefung-nicht-findet",
      title: "Was die Prüfung nicht findet",
      explanation: [
        "Wie die Rechtschreibprüfung erkennt auch die Barrierefreiheitsprüfung nur, was " +
          "sich maschinell feststellen lässt. Ob ein Alternativtext vorhanden ist, kann " +
          "sie prüfen – ob er das Bild sinnvoll beschreibt, nicht.",
        "Drei Dinge musst du selbst im Blick behalten. Erstens: Verlasse dich nie allein " +
          "auf Farbe, um etwas zu kennzeichnen – rote Zahlen für Verluste sind für " +
          "farbfehlsichtige Menschen nicht erkennbar, ein zusätzliches Minuszeichen schon.",
        "Zweitens: Der Kontrast muss stimmen. Hellgrauer Text auf Weiß ist zwar modern, " +
          "aber für viele schwer lesbar.",
        "Drittens: Aussagekräftige Linktexte. „Hier klicken“ hilft niemandem, der sich " +
          "eine Liste aller Links vorlesen lässt – „Jahresbericht 2025 herunterladen“ " +
          "schon.",
      ],
      keyPoints: [
        "Die Prüfung sieht Vorhandensein, nicht Sinnhaftigkeit.",
        "Farbe nie als einziges Unterscheidungsmerkmal.",
        "Linktexte müssen ohne den umgebenden Satz verständlich sein.",
      ],
    },
  ],
};
